import { useEffect, useState } from "react";
import { Volume2, Square, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "./Brand";
import { api } from "@/lib/api";
import { readAloud, stopReading, queueRead, cancelRead, DEFAULT_READ } from "@/lib/readAloud";
import { usePrefs } from "@/lib/store";

export function ListenButton({ text, testId = "listen-button", autoPlay = false }) {
  const [state, setState] = useState("idle");
  const { data: prefs, isFetched } = usePrefs();
  const read = { ...DEFAULT_READ, ...(prefs?.readAloud || {}) };
  useEffect(() => () => cancelRead(text), [text]);
  useEffect(() => {
    if (!autoPlay || !text || !isFetched || !read.autoPlay) return;
    queueRead(text, setState, read.speed);
    return () => cancelRead(text);
  }, [autoPlay, text, isFetched, read.autoPlay, read.speed]);
  if (!text) return null;
  const active = state === "loading" || state === "playing" || state === "queued";
  return (
    <button type="button" onClick={() => (active ? stopReading() : readAloud(text, setState, read.speed))} data-testid={testId} data-state={state}
      className={`self-start inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[13px] font-semibold transition-colors ${active ? "bg-t-brand text-t-onbrand" : "bg-t-surface text-t-brand border border-t-border hover:border-t-brand"}`}>
      {state === "loading" ? <Spinner className="w-4 h-4" /> : state === "playing" ? <Square size={13} fill="currentColor" /> : <Volume2 size={15} />}
      {state === "loading" ? "Preparing voice…" : state === "playing" ? "Stop" : state === "queued" ? "Up next · Stop" : state === "error" ? "Try listening again" : "Listen"}
    </button>
  );
}

export function FeedbackBar({ kind, sessionId, testId = "feedback" }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const send = async (r, c = "") => {
    try {
      await api.post("/feedback", { kind, rating: r, sessionId, comment: c });
      return true;
    } catch {
      toast.error("Couldn't send feedback");
      return false;
    }
  };
  const pick = async (r) => {
    setRating(r);
    if (r === "helped" && (await send(r))) setSent(true);
  };
  const sendComment = async () => {
    if (await send("not_quite", comment.trim())) setSent(true);
  };
  if (sent) return <p className="text-[13px] text-t-muted" data-testid={`${testId}-thanks`}>Thank you — your feedback helps make these reflections better.</p>;
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[13px] text-t-muted mr-1">Did this feel right?</span>
        {[["helped", "This helped", ThumbsUp], ["not_quite", "Not quite", ThumbsDown]].map(([r, l, Icon]) => (
          <button key={r} type="button" onClick={() => pick(r)} data-testid={`${testId}-${r}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[13px] font-semibold border transition-colors ${rating === r ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface text-t-on2 border-t-border hover:border-t-brand"}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>
      {rating === "not_quite" ? (
        <div className="flex flex-col gap-2">
          <textarea className="tf-input !min-h-[64px] text-sm" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000}
            placeholder="What didn't feel right? (optional — shared with the Triggerific-Pilot team)" data-testid={`${testId}-comment`} />
          <button type="button" onClick={sendComment} data-testid={`${testId}-send`} className="self-start rounded-full bg-t-brand text-t-onbrand px-4 h-9 text-sm font-semibold">Send feedback</button>
        </div>
      ) : null}
    </div>
  );
}

// Card header: title on the left, Listen at the top-right so it's seen instantly.
export function NoteHeader({ title, text, testId, className = "", autoPlay = true }) {
  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      <div className="min-w-0 flex-1 pt-1.5">{title}</div>
      {text ? <div className="shrink-0"><ListenButton text={text} testId={testId} autoPlay={autoPlay} /></div> : null}
    </div>
  );
}

// Quality feedback under an AI-written note.
export function AiNoteActions({ text, kind, sessionId, testId }) {
  if (!text) return null;
  return (
    <div className="mt-4 flex flex-col gap-3 pt-3 border-t border-t-border/70">
      {kind ? <FeedbackBar kind={kind} sessionId={sessionId} testId={`${testId}-feedback`} /> : null}
    </div>
  );
}
